import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../shared/api/client";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Label,
  LoadingState,
  Select,
  StatusBadge,
} from "../../../shared/ui";
import type { EmployeeScorecard as Scorecard } from "../../../shared/types/api";
import { Figure, days, percent, rate } from "./reportBits";
import { awaitingAfterDevelopment } from "../../workitems/workItemVocabulary";
import {
  FilterBar,
  defaultFilters,
  toQuery,
  titleize,
  useEmployeeOptions,
  type ReportFilterValues,
} from "./reportFilters";

/**
 * One person over a window: what they finished, how much of it was on time, what is still open and
 * overdue, how their logged time compared to their estimates, and how long their work took.
 *
 * <p>Nothing here is a second copy — every figure is the person's own work items and worklogs, the
 * same rows their board and timesheet show, so a number on this card can be walked back to the
 * task that produced it.
 */
export function EmployeeScorecard() {
  const [employeeId, setEmployeeId] = useState("");
  const [filters, setFilters] = useState<ReportFilterValues>(defaultFilters);
  const employees = useEmployeeOptions();

  const report = useQuery({
    queryKey: ["report", "scorecard", employeeId, filters],
    queryFn: () =>
      api.get<Scorecard>(
        `/api/v1/reports/employee-scorecard?${toQuery(filters, { employeeId })}`,
      ),
    enabled: employeeId !== "",
  });

  return (
    <div className="space-y-4">
      <div className="max-w-sm">
        <Field>
          <Label htmlFor="scorecard-employee">Employee</Label>
          <Select
            id="scorecard-employee"
            value={employeeId}
            onChange={(event) => setEmployeeId(event.target.value)}
          >
            <option value="">Choose a person…</option>
            {(employees.data?.content ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.fullName} · {person.department}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {employeeId === "" ? (
        <EmptyState
          title="Pick someone to see their scorecard"
          message="Choose an employee above, then set the window and any filters."
        />
      ) : (
        <>
          <FilterBar values={filters} onChange={setFilters} showDepartment={false} />
          {report.isPending ? (
            <LoadingState label="Building the scorecard" />
          ) : report.isError ? (
            <ErrorState error={report.error} onRetry={() => void report.refetch()} />
          ) : (
            <Body data={report.data} />
          )}
        </>
      )}
    </div>
  );
}

function Body({ data }: { data: Scorecard }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure
          label="Completed"
          value={String(data.completed)}
          note={`${data.completedOnTime} on time · ${data.completedLate} late`}
        />
        {/* Beside the on-time rate deliberately. Each round of rework is judged against the date
            agreed for that round, so somebody who met every new date reads as perfectly on time —
            true, and only half of what happened. This is the other half. */}
        <Figure
          label="On-time rate"
          value={rate(data.onTimePercent)}
          note={
            data.qaReturns > 0
              ? `of the ones with a deadline · ${data.qaReturns} QA return${
                  data.qaReturns === 1 ? "" : "s"
                }`
              : "of the ones with a deadline"
          }
          tone={
            data.onTimePercent == null
              ? undefined
              : data.onTimePercent >= 80
                ? "green"
                : data.onTimePercent >= 50
                  ? "amber"
                  : "red"
          }
        />
        <Figure
          label="Open now"
          value={String(data.openCount)}
          note={`${data.overdueOpenCount} overdue`}
          tone={data.overdueOpenCount > 0 ? "red" : undefined}
        />
        <Figure
          label="Logged"
          value={`${Math.round(data.loggedHours * 10) / 10}h`}
          note={`estimate ${percent(data.estimateOverrunPercent)} · cycle ${days(
            data.averageCycleTimeDays,
          )}`}
          tone={
            data.estimateOverrunPercent != null && data.estimateOverrunPercent > 0
              ? "amber"
              : undefined
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Missed or overdue" subtitle="Late finishes and open work past due." />
          <CardBody className="p-0">
            {data.lateItems.length === 0 ? (
              <EmptyState title="Nothing late" message="No late or overdue items in this window." />
            ) : (
              <ul className="divide-y divide-ink-100">
                {data.lateItems.map((item) => (
                  <li key={item.workItemId} className="flex flex-wrap items-baseline gap-2 px-4 py-2.5 text-xs">
                    <Link
                      to={`/work-items/${item.itemKey}`}
                      className="font-mono text-ink-500 hover:text-brand-600"
                    >
                      {item.itemKey}
                    </Link>
                    <span className="text-ink-700">{item.title}</span>
                    <span className={item.stillOpen ? "text-red-700" : "text-amber-700"}>
                      {item.daysLate}d {item.stillOpen ? "overdue" : "late"}
                    </span>
                    {awaitingAfterDevelopment(item.status) && (
                      <span className="text-ink-500">
                        · {awaitingAfterDevelopment(item.status)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Over estimate" subtitle="Logged time past the original estimate." />
          <CardBody className="p-0">
            {data.overrunItems.length === 0 ? (
              <EmptyState title="Within estimate" message="No estimated item ran over." />
            ) : (
              <ul className="divide-y divide-ink-100">
                {data.overrunItems.map((item) => (
                  <li key={item.workItemId} className="flex flex-wrap items-baseline gap-2 px-4 py-2.5 text-xs">
                    <Link
                      to={`/work-items/${item.itemKey}`}
                      className="font-mono text-ink-500 hover:text-brand-600"
                    >
                      {item.itemKey}
                    </Link>
                    <span className="text-ink-700">{item.title}</span>
                    <span className="tnum text-ink-500">
                      {item.estimatedHours}h → {item.actualHours}h
                    </span>
                    <span className="tnum text-red-700">{percent(item.overrunPercent)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Open work" subtitle="Everything currently on their plate, overdue first." />
        <CardBody className="p-0">
          {data.openItems.length === 0 ? (
            <EmptyState title="Nothing open" message="No open items assigned in this filter." />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.openItems.map((item) => (
                <li key={item.workItemId} className="flex flex-wrap items-baseline gap-2 px-4 py-2.5 text-xs">
                  <Link
                    to={`/work-items/${item.itemKey}`}
                    className="font-mono text-ink-500 hover:text-brand-600"
                  >
                    {item.itemKey}
                  </Link>
                  <span className="text-ink-700">{item.title}</span>
                  <StatusBadge tone={item.overdue ? "red" : "gray"}>
                    {titleize(item.status)}
                  </StatusBadge>
                  <span className="text-ink-400">due {item.deadline ?? "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
